import { useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, Clock3, Euro, Factory, Gauge, TrendingUp, Users } from "lucide-react";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const BIG = "mt-3 font-display text-3xl tracking-tight text-bone";

export const DashboardOverview = ({ onOpenModule }) => {
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get("/dashboard");
                setData(data);
            } catch {}
        })();
    }, []);

    if (!data) {
        return <p data-testid="dashboard-loading" className="mt-10 text-xs uppercase tracking-[0.3em] text-ash">Cijfers laden…</p>;
    }

    const p = data.production;
    const c = data.crm;
    const pl = data.planning;
    const inv = data.invoicing;
    const actieveOrders = p ? (p.orders_by_status?.nieuw || 0) + (p.orders_by_status?.in_productie || 0) + (p.orders_by_status?.wacht_materiaal || 0) : 0;

    return (
        <div data-testid="dashboard-overview" className="mt-10">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {p ? (
                    <button data-testid="dash-production" onClick={() => onOpenModule("production")} className={`${CARD} text-left transition-colors hover:border-gold/40`}>
                        <p className={LABEL}><Factory className="mr-2 inline h-3.5 w-3.5 text-gold" />Productie</p>
                        <p className={BIG}>{actieveOrders} <span className="text-base text-ash">actieve orders</span></p>
                        <p className="mt-2 flex items-center gap-2 text-xs text-ash">
                            <Clock3 className="h-3 w-3" /> {p.hours_week} uur deze week
                            {p.due_this_week ? <span className="text-gold">· {p.due_this_week} deadline(s)</span> : null}
                        </p>
                    </button>
                ) : null}
                {c ? (
                    <button data-testid="dash-crm" onClick={() => onOpenModule("crm")} className={`${CARD} text-left transition-colors hover:border-gold/40`}>
                        <p className={LABEL}><TrendingUp className="mr-2 inline h-3.5 w-3.5 text-gold" />Pipeline</p>
                        <p className={BIG}>{euro(c.pipeline_value)}</p>
                        <p className="mt-2 flex items-center gap-2 text-xs text-ash">
                            <Users className="h-3 w-3" /> {c.customers} klanten
                            {c.overdue_followups ? (
                                <span data-testid="dash-crm-overdue" className="flex items-center gap-1 text-red-300">
                                    <AlertTriangle className="h-3 w-3" /> {c.overdue_followups} opvolging verlopen
                                </span>
                            ) : null}
                        </p>
                    </button>
                ) : null}
                {pl ? (
                    <button data-testid="dash-planning" onClick={() => onOpenModule("planning")} className={`${CARD} text-left transition-colors hover:border-gold/40`}>
                        <p className={LABEL}><CalendarClock className="mr-2 inline h-3.5 w-3.5 text-gold" />Vandaag aan het werk</p>
                        <p className={BIG}>{pl.working_today.length} <span className="text-base text-ash">mensen</span></p>
                        <p data-testid="dash-planning-names" className="mt-2 truncate text-xs text-ash">
                            {pl.working_today.length ? pl.working_today.join(", ") : "Niemand ingepland vandaag"}
                            {pl.on_leave_today ? <span className="text-gold"> · {pl.on_leave_today} met verlof</span> : null}
                        </p>
                    </button>
                ) : null}
                {p?.oee ? (
                    <button data-testid="dash-oee" onClick={() => onOpenModule("production")} className={`${CARD} text-left transition-colors hover:border-gold/40`}>
                        <p className={LABEL}><Gauge className="mr-2 inline h-3.5 w-3.5 text-gold" />OEE — 30 dagen</p>
                        <p className={BIG}>
                            <span className={p.oee.oee >= (p.oee_target || 85) ? "text-gold" : "text-bone"}>{p.oee.oee}%</span>
                            <span className="text-base text-ash"> van {p.oee_target || 85}% doel</span>
                        </p>
                        <p className="mt-2 text-xs text-ash">
                            B {p.oee.availability ?? "—"}% · P {p.oee.performance ?? "—"}% · K {p.oee.quality ?? "—"}%
                        </p>
                    </button>
                ) : null}
                {inv ? (
                    <button data-testid="dash-invoicing" onClick={() => onOpenModule("invoicing")} className={`${CARD} text-left transition-colors hover:border-gold/40`}>
                        <p className={LABEL}><Euro className="mr-2 inline h-3.5 w-3.5 text-gold" />Openstaand</p>
                        <p className={BIG}>{euro(inv.open_amount)}</p>
                        <p className="mt-2 text-xs text-ash">
                            Offertes in de markt: <span className="text-bone">{euro(inv.offertes_open)}</span>
                            {inv.overdue_count ? <span className="text-red-300"> · {inv.overdue_count} verlopen</span> : null}
                        </p>
                    </button>
                ) : null}
            </div>
        </div>
    );
};
